/**
 * Protocol scheduling rule data shape and constants.
 * Recurrence: daily | weekly | monthly | yearly.
 * Times: list of { hour, minute } per day (24h).
 * Weekly: daysOfWeek (0=Sunday .. 6=Saturday).
 * Monthly: dayOfMonth 1-31 OR weekInMonth (1|2|3|4|'last') + dayOfWeek 0-6.
 * Yearly: month 1-12, dayOfMonth 1-31, optional time(s).
 */

export const RECURRENCE = {
  DAILY: 'daily',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  YEARLY: 'yearly'
};

export const WEEK_IN_MONTH = {
  FIRST: 1,
  SECOND: 2,
  THIRD: 3,
  FOURTH: 4,
  LAST: 'last'
};

/** Sunday=0 .. Saturday=6 */
export const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' }
];

/** Schedule type codes from nomenclature (frequency_types). Only SCHED uses recurrence/times. */
export const SCHEDULE_TYPE_SCHED = 'SCHED';
/** Triggered type: protocol runs when trigger events fire. */
export const SCHEDULE_TYPE_TRIG = 'TRIG';
/** PRD 5.6: Risk-Driven — frequency resolved at task generation from location criticality + nomenclature. */
export const SCHEDULE_TYPE_RISK_DRIVEN = 'RISK_DRIVEN';

/**
 * Default empty rule (new rule shape).
 */
export function createEmptyScheduleRule() {
  return {
    id: Date.now().toString(),
    frequency: '', // Schedule type: SCHED, TRIG, CONT, ONCE, ON_DEMAND. Recurrence/times only for SCHED.
    recurrenceType: RECURRENCE.DAILY,
    times: [{ hour: 8, minute: 0 }],
    // weekly
    daysOfWeek: [],
    // monthly: either dayOfMonth (1-31) OR (weekInMonth + dayOfWeek)
    dayOfMonth: null,
    weekInMonth: null,
    dayOfWeek: null,
    // yearly
    month: null,
    yearDayOfMonth: null,
    timeConstraints: {
      maxDuration: 30,
      timeoutAction: 'escalate',
      escalationContact: ''
    },
    triggerEvents: [],
    saved: false
  };
}

/**
 * Normalize legacy rule (frequency/frequencyValue) to new shape for display; does not mutate.
 */
export function normalizeLegacyRule(rule) {
  const r = { ...rule };
  if (!r.frequency && r.recurrenceType) r.frequency = SCHEDULE_TYPE_SCHED;
  if (!r.frequency && (r.times?.length || r.recurrenceType)) r.frequency = SCHEDULE_TYPE_SCHED;
  r.recurrenceType = r.recurrenceType || RECURRENCE.DAILY;
  r.times = (r.times && r.times.length) ? r.times : [{ hour: 8, minute: 0 }];
  r.daysOfWeek = r.daysOfWeek || [];
  r.dayOfMonth = r.dayOfMonth ?? null;
  r.weekInMonth = r.weekInMonth ?? null;
  r.dayOfWeek = r.dayOfWeek ?? null;
  r.month = r.month ?? null;
  r.yearDayOfMonth = r.yearDayOfMonth ?? null;
  return r;
}

/** True when this rule uses the recurrence/times schedule (Scheduled type). */
export function isScheduledType(rule) {
  const r = rule || {};
  return (r.frequency === SCHEDULE_TYPE_SCHED) || (!r.frequency && (r.recurrenceType || (r.times && r.times.length)));
}

/** True when this rule is Triggered (shows trigger events). */
export function isTriggeredType(rule) {
  const r = rule || {};
  return r.frequency === SCHEDULE_TYPE_TRIG;
}

/** True when this rule is Risk-Driven (frequency from location criticality at task generation). */
export function isRiskDrivenType(rule) {
  const r = rule || {};
  return r.frequency === SCHEDULE_TYPE_RISK_DRIVEN;
}

/**
 * Format a time slot for display (24h). E.g. 08:00, 21:00.
 */
export function formatTimeSlot(slot) {
  if (!slot) return '—';
  const h = Number(slot.hour);
  const m = Number(slot.minute);
  const hh = String(Number.isNaN(h) ? 0 : h).padStart(2, '0');
  const mm = String(Number.isNaN(m) ? 0 : m).padStart(2, '0');
  return `${hh}:${mm}`;
}

/**
 * Parse "HH:MM" or "H:MM" to { hour, minute }.
 */
export function parseTimeSlot(str) {
  if (!str || typeof str !== 'string') return null;
  const parts = str.trim().split(':');
  if (parts.length < 2) return null;
  const hour = parseInt(parts[0], 10);
  const minute = parseInt(parts[1], 10);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return null;
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  return { hour, minute };
}
